import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { agentThreadQueryKey } from "@/shared/domain/agents/hooks/useAgentThread";

import { AgentThreadRecord, ThreadVisibility } from "../AgentThreadRecord";

import { activeAgentThreadQueryKeyPrefix } from "./useActiveAgentThread";

interface UpdateThreadVisibilityParams {
  threadId: string;
  visibility: ThreadVisibility;
}

/**
 * React Query hook for re-scoping who may read a thread.
 *
 * Creator-only on the backend, so callers should render the affordance only
 * for the signed-in user's own threads rather than relying on the 401.
 *
 * Patches the cached records rather than relying on invalidation alone: once
 * the sidebar's or the detail page's thread query holds a continuation token
 * it refetches through the delta endpoint, whose response carries messages, status, title, goals and
 * tasks but no visibility, so the merged record keeps whatever visibility the
 * cache already held. An invalidate-only mutation therefore re-reads the stale
 * value and the UI keeps showing the old state even though the write landed.
 */
export function useUpdateThreadVisibility() {
  const { agentThreadService } = useDomainServices();
  const orgId = useCurrentOrgId();
  const queryClient = useQueryClient();
  const showNotification = useNotification();

  return useMutation<void, Error, UpdateThreadVisibilityParams>({
    mutationFn: ({ threadId, visibility }) =>
      agentThreadService.updateThreadVisibility(threadId, visibility, {
        orgId,
      }),
    onSuccess: async (_result, { threadId, visibility }) => {
      const activeThreadKey = activeAgentThreadQueryKeyPrefix(threadId);
      const detailKey = agentThreadQueryKey(threadId);

      // A poll already in flight when the write lands resolves from the
      // snapshot it took beforehand, and the delta merge carries that
      // snapshot's visibility, so it would put the old value back over the
      // patch. Awaiting the cancel lets the request abort and its query state
      // settle first.
      await Promise.all([
        queryClient.cancelQueries({ queryKey: activeThreadKey }),
        queryClient.cancelQueries({ queryKey: detailKey }),
      ]);

      const patch = (record: AgentThreadRecord | undefined) =>
        record === undefined ? record : { ...record, visibility };

      queryClient.setQueriesData<AgentThreadRecord>(
        { queryKey: activeThreadKey },
        patch,
      );
      queryClient.setQueryData<AgentThreadRecord>(detailKey, patch);

      // Key prefixes, so every org / page / filter variant refetches: the lists
      // hold their own copies of the record, and a visibility change can change
      // which rows a filtered search returns, not just one row's value.
      void queryClient.invalidateQueries({ queryKey: ["agentThreadsForUser"] });
      void queryClient.invalidateQueries({ queryKey: ["agentThreadSearch"] });
    },
    onError: () => {
      // A failed write leaves the picker showing the old state, which is
      // indistinguishable from a click that never registered.
      showNotification("Couldn't change who can read this thread", "error");
    },
  });
}
